import React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonBackButton,
  IonButton,
  IonContent,
  IonCard,
  IonCardContent,
  IonIcon,
  IonSpinner,
  IonText,
} from '@ionic/react';
import { addOutline, closeOutline } from 'ionicons/icons';
import { useLotForm } from '../../application/use-cases/lots/useLotForm';
import { hasComponent } from '../../domain/Component';
import './Lots.css';

function localToday(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const numberOrNull = (v: string) => (v.trim() === '' ? null : Number(v));

// Alta y edición de un lote: datos, cultivo con su tabla de etapas
// (precargada y editable: cada variedad y fecha de siembra cambian los
// números) y las estaciones que tiene.
// admin: abierto desde el panel de admin (edita el lote como el dueño).
const LotForm: React.FC<{ admin?: boolean }> = ({ admin = false }) => {
  const { id } = useParams<{ id?: string }>();
  const history = useHistory();
  const base = admin ? '/app/admin/lots' : '/app/lots';
  const {
    form,
    set,
    presets,
    sites,
    stations,
    lotOfStation,
    canEditStations,
    applyPreset,
    setStage,
    addStage,
    removeStage,
    toggleStation,
    loading,
    saving,
    error,
    save,
  } = useLotForm(id, admin);

  const onSave = async () => {
    const lot = await save();
    if (lot) history.replace(`${base}/${lot.id}`);
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref={id ? `${base}/${id}` : base} />
          </IonButtons>
          <IonTitle>{id ? (admin ? 'Editar lote (admin)' : 'Editar lote') : 'Nuevo lote'}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen>
        {loading ? (
          <div className="ion-text-center ion-padding">
            <IonSpinner color="primary" />
          </div>
        ) : (
          <div className="lot-form">
            <IonCard>
              <IonCardContent>
                <label className="lot-field">
                  <span>Nombre</span>
                  <input value={form.name} placeholder="Lote 1" onChange={(e) => set('name', e.target.value)} />
                </label>
                <div className="lot-field-row">
                  <label className="lot-field">
                    <span>Superficie (ha)</span>
                    <input
                      type="number"
                      min={0}
                      step="any"
                      value={form.areaHa ?? ''}
                      onChange={(e) => set('areaHa', numberOrNull(e.target.value))}
                    />
                  </label>
                  <label className="lot-field">
                    <span>Campo</span>
                    <select value={form.siteId ?? ''} onChange={(e) => set('siteId', e.target.value || null)}>
                      <option value="">Sin campo</option>
                      {sites.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              </IonCardContent>
            </IonCard>

            <IonCard>
              <IonCardContent>
                <p className="lot-section-title">Cultivo</p>
                {!id && (
                  <div className="lot-presets">
                    {presets.map((p) => (
                      <button key={p.key} className={form.crop === p.crop ? 'is-active' : ''} onClick={() => applyPreset(p.key)}>
                        {p.crop}
                      </button>
                    ))}
                  </div>
                )}
                <div className="lot-field-row">
                  <label className="lot-field">
                    <span>Cultivo / variedad</span>
                    <input value={form.crop} onChange={(e) => set('crop', e.target.value)} />
                  </label>
                  <label className="lot-field">
                    <span>Temperatura base (°C)</span>
                    <input
                      type="number"
                      step={0.5}
                      value={form.baseTemp}
                      onChange={(e) => set('baseTemp', numberOrNull(e.target.value) ?? 0)}
                    />
                  </label>
                </div>
                <label className="lot-field">
                  <span>Fecha de siembra (o del último corte o pastoreo)</span>
                  <input
                    type="date"
                    max={localToday()}
                    value={form.cycleStart ?? ''}
                    onChange={(e) => set('cycleStart', e.target.value || null)}
                  />
                </label>

                <p className="lot-section-title" style={{ marginTop: 14 }}>
                  Etapas
                </p>
                <p className="lot-detail-note">
                  Grados día acumulados desde la siembra en que empieza cada etapa. Son valores de referencia: ajustalos a la
                  variedad y la fecha de siembra.
                </p>
                <div className="lot-stage-editor">
                  <div className="lot-stage-editor-head">
                    <span>Etapa</span>
                    <span>Zadoks</span>
                    <span>°Cd</span>
                    <span />
                  </div>
                  {form.stages.map((stage, i) => (
                    <div className="lot-stage-editor-row" key={i}>
                      <input aria-label="Etapa" value={stage.name} onChange={(e) => setStage(i, { name: e.target.value })} />
                      <input
                        aria-label="Zadoks"
                        value={stage.zadoks ?? ''}
                        onChange={(e) => setStage(i, { zadoks: e.target.value || null })}
                      />
                      <input
                        aria-label="Grados día"
                        type="number"
                        min={0}
                        value={stage.gdd}
                        disabled={i === 0}
                        onChange={(e) => setStage(i, { gdd: numberOrNull(e.target.value) ?? 0 })}
                      />
                      <button aria-label="Quitar etapa" disabled={i === 0 || form.stages.length <= 2} onClick={() => removeStage(i)}>
                        <IonIcon icon={closeOutline} />
                      </button>
                    </div>
                  ))}
                </div>
                <IonButton fill="clear" size="small" onClick={addStage}>
                  <IonIcon icon={addOutline} slot="start" />
                  Agregar etapa
                </IonButton>
              </IonCardContent>
            </IonCard>

            {canEditStations && (
              <IonCard>
                <IonCardContent>
                  <p className="lot-section-title">Estaciones del lote</p>
                  <p className="lot-detail-note">
                    La temperatura y la lluvia del lote son el promedio de sus estaciones. Cada estación está en un solo lote.
                  </p>
                  {stations.length === 0 && (
                    <p className="lot-detail-note">{admin ? 'El dueño no tiene estaciones propias.' : 'No tenés estaciones propias para sumar.'}</p>
                  )}
                  <ul className="lot-station-picker">
                    {stations.map((s) => {
                      const other = lotOfStation[s.id];
                      const elsewhere = other && other.id !== id;
                      const checked = form.stationIds.includes(s.id);
                      const kinds = [hasComponent(s.components, 'air') ? 'clima' : null, hasComponent(s.components, 'rain') ? 'pluviómetro' : null]
                        .filter(Boolean)
                        .join(' · ');
                      return (
                        <li key={s.id}>
                          <label>
                            <input type="checkbox" checked={checked} onChange={() => toggleStation(s.id)} />
                            <span>
                              {s.name}
                              <small>
                                {kinds || 'sin clima ni lluvia'}
                                {elsewhere && (checked ? ` · se mueve desde ${other.name}` : ` · en ${other.name}`)}
                              </small>
                            </span>
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                </IonCardContent>
              </IonCard>
            )}

            {error && (
              <IonText color="danger">
                <p className="lot-form-error">{error}</p>
              </IonText>
            )}
            <IonButton expand="block" className="lot-form-save" disabled={saving} onClick={onSave}>
              {saving ? 'Guardando...' : id ? 'Guardar' : 'Crear lote'}
            </IonButton>
          </div>
        )}
      </IonContent>
    </IonPage>
  );
};

export default LotForm;
